import { animate, style, transition, trigger } from '@angular/animations';
import { DecimalPipe } from '@angular/common';
import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { getBuildIdQuery, IFilterPageLayout, IFilterParams, IFilterSort, IFilterView, IPremiumContentSelection, ISelectedFacets } from '@ncg/data';
import { TranslateService } from '@ngx-translate/core';
import { combineLatest, firstValueFrom, Subject } from 'rxjs';
import { distinctUntilChanged, take, takeUntil } from 'rxjs/operators';

import { filterParams, layout, selectedPremiumText } from '../../+state/products/products.actions';
import { ProductsFacade } from '../../+state/products/products.facade';
import { FeatureDetectionService } from '../../core/feature-detection.service';
import { SettingsService } from '../../core/settings.service';
import { FilterService } from '../../filter/filter.service';
import { SidePanelService } from '../../side-panel/side-panel.service';
import { translateFadeIn } from '../../utils/animations/slide.animations';

@Component({
    selector: 'ncg-product-sorting',
    template: `
        <div class="is-hidden-desktop" *ngIf="itemsAmount">
            <div class="columns is-mobile is-gapless is-multiline">
                <div *ngIf="title" class="column is-12">
                    <h1 class="has-text-black has-text-centered pb-5">{{ title }}</h1>
                </div>
                <div class="column is-12">
                    <ncg-product-search></ncg-product-search>
                </div>
                <div class="column is-12">
                    <div class="level level-item mb-2">
                        <div class="product-sorting__amount" *ngIf="itemsAmount || itemsAmount === 0">
                            {{ 'product_list.amount_used_models' | translate: { amount: itemsAmount, make: amountText } }}
                        </div>
                        <div class="px-2">
                            <ncg-my-profile-button></ncg-my-profile-button>
                        </div>
                        <div class="px-2">
                            <ncg-favorites-page-link
                                [showCount]="true"
                                [linkClass]="'is-rounded is-product-sorting'"
                                [textClass]="'is-hidden'"
                            ></ncg-favorites-page-link>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div class="product-sorting" [ngClass]="{ 'product-sorting--small-padding': filterPageLayout === 'filterVisible' }">
            <div class="columns is-gapless is-vcentered is-hidden-touch is-marginless">
                <div class="column">
                    <div class="product-sorting__amount product-sorting__text" *ngIf="itemsAmount || itemsAmount === 0">
                        {{ 'product_list.amount_used_models' | translate: { amount: itemsAmount, make: amountText } }}
                    </div>
                </div>
                <div class="column is-narrow" *ngIf="isPremiumEnabled && premium">
                    <div class="product-sorting__item product-sorting__text field is-marginless">
                        <div class="toggle level">
                            <input type="checkbox" id="premium" (change)="togglePremiumView()" [(ngModel)]="isPremium" />
                            <label for="premium"></label>
                            <span>{{ premium }}</span>
                            <button *ngIf="premiumUrl" type="button" class="button-icon is-primary" (click)="openSidepanel()">
                                <span class="icon">
                                    <svg-icon-sprite
                                        [src]="'assets/images/sprite-multi.svg' + buildIdQuery + '#questionmark'"
                                        [viewBox]="'0 0 30 30'"
                                        [width]="'30px'"
                                        [height]="'30px'"
                                        aria-hidden="true"
                                    ></svg-icon-sprite>
                                </span>
                            </button>
                        </div>
                    </div>
                </div>
                <div class="column is-narrow" *ngIf="filterPageLayout === 'filterInSidepanel'">
                    <div class="product-sorting__item field is-marginless">
                        <div class="toggle level">
                            <input type="checkbox" id="leasingToggle" (change)="toggleLeasingView()" [(ngModel)]="isLeasing" />
                            <label for="leasingToggle"></label>
                            <span>{{ 'product_list.leasing' | translate }}</span>
                        </div>
                    </div>
                </div>
                <div class="column is-narrow custom-dropdown" *ngIf="sortingsList?.length">
                    <ng-container
                        *ngTemplateOutlet="
                            productSortTmpl;
                            context: { className: 'button is-clean product-sorting__button', title: modelSorting?.title, isMobile: false }
                        "
                    ></ng-container>
                </div>
                <div class="column is-narrow">
                    <div class="product-sorting__item field columns is-marginless is-gapless">
                        <div class="column">
                            <button
                                type="button"
                                class="button is-clean product-sorting__view-btn has-space-right"
                                aria-label="Vis bilerne i et grid"
                                (click)="toggleFilterView('grid')"
                                [ngClass]="{ 'is-active': view === 'grid' }"
                            >
                                <svg-icon-sprite
                                    src="grid"
                                    [viewBox]="'0 0 30 30'"
                                    [width]="'30px'"
                                    [height]="'30px'"
                                    aria-hidden="true"
                                    classes=""
                                ></svg-icon-sprite>
                            </button>
                        </div>
                        <div class="column">
                            <button
                                type="button"
                                class="button is-clean product-sorting__view-btn"
                                aria-label="Vis bilerne i en liste"
                                (click)="toggleFilterView('list')"
                                [ngClass]="{ 'is-active': view === 'list' }"
                            >
                                <svg-icon-sprite
                                    src="list"
                                    [viewBox]="'0 0 30 30'"
                                    [width]="'30px'"
                                    [height]="'30px'"
                                    aria-hidden="true"
                                    classes=""
                                ></svg-icon-sprite>
                            </button>
                        </div>
                    </div>
                </div>
                <div *ngIf="filterPageLayout === 'filterVisible'" class="column is-narrow">
                    <ncg-my-profile-button></ncg-my-profile-button>
                    <ncg-favorites-page-link
                        class="ml-4"
                        [showCount]="true"
                        [linkClass]="'is-rounded is-product-sorting'"
                        [textClass]="'is-hidden'"
                    ></ncg-favorites-page-link>
                </div>
            </div>

            <div #stickyTriggerElement class="level level-item is-hidden-desktop is-justify-content-space-between mb-2">
                <ng-container *ngTemplateOutlet="filterButtons; context: { className: 'is-filter' }"></ng-container>
            </div>

            <div class="selected-filter" *ngIf="selectedFacets.length">
                <div class="selected-filter__item selected-filter__clear">
                    <button
                        type="button"
                        class="button"
                        [ngClass]="filterPageLayout === 'filterInSidepanel' ? 'is-white' : 'is-border'"
                        (click)="clearAllFilters()"
                    >
                        {{ 'product_list.reset' | translate }}
                    </button>
                </div>
                <div class="selected-filter__item" *ngFor="let facet of selectedFacets; trackBy: trackByFacet">
                    <button
                        type="button"
                        class="button"
                        [ngClass]="{
                            'is-white': filterPageLayout === 'filterInSidepanel',
                            'is-border': filterPageLayout !== 'filterInSidepanel',
                            'is-fetching': currentKey === facet.key && currentValue === facet.value && isFetching
                        }"
                        (click)="updateFilter(facet.key, facet.value)"
                    >
                        <ng-container [ngSwitch]="facet.key">
                            <ng-container *ngSwitchCase="'mileage'">{{ 'facet_types.mileage' | translate }}: {{ facet.formattedValue }}</ng-container>
                            <ng-container *ngSwitchCase="'effect'">{{ 'facet_types.effect' | translate }}: {{ facet.formattedValue }}</ng-container>
                            <ng-container *ngSwitchCase="'year'">{{ 'facet_types.year' | translate }}: {{ facet.formattedValue }}</ng-container>
                            <ng-container *ngSwitchCase="'price'">{{ 'facet_types.price' | translate }}: {{ facet.formattedValue }}</ng-container>
                            <ng-container *ngSwitchCase="'iscash'">{{ 'facet_types.iscash' | translate }}</ng-container>
                            <ng-container *ngSwitchCase="'isleasing'">{{ 'facet_types.isleasing' | translate }}</ng-container>
                            <ng-container *ngSwitchCase="'isdemo'">
                                {{ (facet.value ? 'facet_types.isdemo' : 'facet_types.isnotdemo') | translate }}
                            </ng-container>
                            <ng-container *ngSwitchCase="'ispremium'">{{ premium }}</ng-container>
                            <ng-container *ngSwitchCase="'series.keyword'">{{ facet.formattedValue }}-serie</ng-container>
                            <ng-container *ngSwitchCase="'q'">{{ 'facet_types.query' | translate: { query: facet.formattedValue } }}</ng-container>
                            <ng-container *ngSwitchCase="'haspictures'">{{ 'facet_types.has_pictures' | translate }}</ng-container>
                            <ng-container *ngSwitchDefault>{{ facet.formattedValue }}</ng-container>
                        </ng-container>
                        <img [src]="'assets/images/close_light.svg' + buildIdQuery" [alt]="facet.formattedValue" />
                    </button>
                </div>
            </div>
        </div>

        <div
            *ngIf="isButtonsSticky"
            class="product-sorting__sticky-buttons level level-item is-hidden-desktop"
            [@translateFadeIn]="{ value: isButtonsSticky, params: { y: '-200%' } }"
            aria-hidden="true"
        >
            <ng-container *ngTemplateOutlet="filterButtons; context: { className: 'is-primary' }"></ng-container>
        </div>

        <ng-template #filterButtons let-className="className">
            <div *ngIf="sortingsList?.length" class="custom-dropdown">
                <ng-container
                    *ngTemplateOutlet="
                        productSortTmpl;
                        context: {
                            className: 'button is-rounded product-sorting__filter-button ' + className,
                            title: 'product_list.label_sort_mobile' | translate,
                            isMobile: true
                        }
                    "
                ></ng-container>
            </div>
            <button type="button" class="button is-rounded product-sorting__filter-button" [ngClass]="className" (click)="openFilterPanel()">
                <span> {{ 'filter_spot.label_all_filters_mobile' | translate }}</span>
                <span class="icon">
                    <svg-icon-sprite
                        [src]="'assets/images/sprite-multi.svg' + buildIdQuery + '#filter'"
                        [viewBox]="'0 0 30 30'"
                        [width]="'30px'"
                        [height]="'30px'"
                        aria-hidden="true"
                    ></svg-icon-sprite>
                </span>
            </button>
        </ng-template>

        <ng-template #productSortTmpl let-className="className" let-title="title" let-isMobile="isMobile">
            <div
                class="dropdown"
                [ngClass]="{ 'product-sorting__item': !isMobile }"
                ngbDropdown
                (openChange)="onDropdownClick($event)"
                [display]="'static'"
            >
                <button id="sorting" ngbDropdownToggle [ngClass]="className">
                    <span class="chevron chevron--animate icon" [ngClass]="{ 'chevron--up': isDropdownOpen }" *ngIf="isMobile">
                        <svg-icon-sprite
                            src="chevron-light-down"
                            [viewBox]="'0 0 30 30'"
                            [width]="'30px'"
                            [height]="'30px'"
                            aria-hidden="true"
                            classes=""
                        ></svg-icon-sprite>
                    </span>
                    <span class="has-text-left" [ngClass]="{ 'product-sorting__button--text': !isMobile }">
                        <ng-container *ngIf="!isMobile">{{ 'product_list.label_sort_desktop' | translate }}:</ng-container>
                        {{ title | translate }}
                    </span>
                    <span class="chevron chevron--animate icon has-text-primary" [ngClass]="{ 'chevron--up': isDropdownOpen }" *ngIf="!isMobile">
                        <svg-icon-sprite
                            src="chevron-light-down"
                            [viewBox]="'0 0 30 30'"
                            [width]="'30px'"
                            [height]="'30px'"
                            aria-hidden="true"
                            classes=""
                        ></svg-icon-sprite>
                    </span>
                </button>
                <div ngbDropdownMenu aria-labelledby="sorting" class="dropdown-menu" [@dropdownAnimation] *ngIf="isDropdownOpen">
                    <div ngbDropdownItem>
                        <div class="field" *ngFor="let item of sortingsList; let index = index">
                            <div class="control radio radio--hidden is-block">
                                <input
                                    class="is-hidden"
                                    type="radio"
                                    [id]="'sort_' + index"
                                    name="sort"
                                    [value]="item"
                                    [(ngModel)]="modelSorting"
                                    (change)="toggleSortingView()"
                                />
                                <label [for]="'sort_' + index" class="is-block">
                                    {{ item.title | translate }}
                                </label>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </ng-template>
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
    animations: [
        trigger('dropdownAnimation', [
            transition(':enter', [
                style({
                    height: '0px',
                    overflow: 'hidden',
                    transform: 'translateY(-40px) scaleY(0.8)',
                    opacity: '0',
                }),
                animate(
                    '400ms cubic-bezier(0.22, 1, 0.36, 1)',
                    style({
                        height: '*',
                        overflow: 'hidden',
                        transform: 'translateY(0px) scaleY(1)',
                        opacity: '1',
                    })
                ),
            ]),
            transition(':leave', [
                style({
                    height: '*',
                    overflow: 'hidden',
                    transform: 'translateY(0px)',
                    opacity: '1',
                }),
                animate(
                    '200ms cubic-bezier(0.550, 0.085, 0.680, 0.530)',
                    style({
                        height: '0px',
                        overflow: 'hidden',
                        transform: 'translateY(-10px)',
                        opacity: '0',
                    })
                ),
            ]),
        ]),
        translateFadeIn,
    ],
})
export class ProductSortingComponent implements OnInit, OnDestroy, AfterViewInit {
    private readonly unsubscribe = new Subject<void>();

    @Input() title?: string;

    @ViewChild('stickyTriggerElement')
    public stickyTriggerRef?: ElementRef<HTMLElement>;

    premium?: string;
    premiumUrl?: string;
    itemsAmount?: number;
    amountText?: string;
    key?: string;
    view: IFilterView;
    filterPageLayout?: IFilterPageLayout;
    isPremium = false;
    isLeasing = false;
    modelSorting?: { sort?: IFilterSort; title: string };
    sortingsList: { title: string; sort?: IFilterSort; selected?: boolean }[];
    translations: any = {};
    selectedFacets: ISelectedFacets[] = [];
    filterParams?: IFilterParams;
    isFetching = false;
    currentValue?: string;
    currentKey?: string;
    locale = '';
    premiumContentSelection?: IPremiumContentSelection[];
    isDropdownOpen?: boolean;
    isPremiumEnabled = false;
    buildIdQuery = getBuildIdQuery();

    private stickyButtonsObserver?: IntersectionObserver;
    public isButtonsSticky = false;

    constructor(
        private readonly translate: TranslateService,
        private readonly cd: ChangeDetectorRef,
        private readonly productsFacade: ProductsFacade,
        private readonly filterService: FilterService,
        private readonly settingsService: SettingsService,
        private readonly decimalPipe: DecimalPipe,
        private readonly sidePanelService: SidePanelService,
        private readonly featureDetectionService: FeatureDetectionService
    ) {}

    ngOnInit() {
        this.getSettings();
        this.getLayout();
        this.getFilterQueriesAndTotal();
        this.getSelectedFacets();
        this.getIsFetching();
    }

    ngAfterViewInit(): void {
        this.addStickyObserver();
    }

    onDropdownClick(isOpen: boolean) {
        this.isDropdownOpen = isOpen;
    }

    getSettings() {
        this.settingsService
            .get()
            .pipe(take(1), takeUntil(this.unsubscribe))
            .subscribe((settings) => {
                this.locale = `${settings.seoLanguage}-${settings.seoCountry}`;
                this.premiumContentSelection = settings.premiumContentSelection;
            });
    }

    getLayout() {
        this.productsFacade.layout$.pipe(takeUntil(this.unsubscribe)).subscribe((data) => {
            if (data) {
                this.filterPageLayout = data?.pageLayout;
                this.view = data?.view ?? 'grid';
            }
            this.cd.markForCheck();
        });
    }

    getFilterQueriesAndTotal() {
        combineLatest([
            this.productsFacade.filterParams$,
            this.productsFacade.total$,
            this.productsFacade.isPremiumEnabled$,
            this.productsFacade.premiumText$,
        ])
            .pipe(distinctUntilChanged(), takeUntil(this.unsubscribe))
            .subscribe(([filterData, total, isPremiumEnabled, premiumText]) => {
                this.filterParams = { ...filterData };
                this.isPremiumEnabled = isPremiumEnabled;
                const makeKeywords = filterData?.['make.keyword'];
                const leasing = filterData?.['isleasing'];
                this.isLeasing = (leasing === true || leasing === 'true') ?? false;
                this.itemsAmount = total;
                this.createSortingList(filterData?.sort);

                if (isPremiumEnabled) {
                    this.setPremiumText(premiumText, filterData);
                }

                this.key = this.setKey(makeKeywords);
                if (this.key) {
                    this.amountText = this.key;
                } else {
                    firstValueFrom(this.translate.get('product_list.amount_all_brands').pipe(takeUntil(this.unsubscribe))).then((text) => {
                        this.amountText = text;
                        this.cd.markForCheck();
                    });
                }
                this.cd.markForCheck();
            });
    }

    setKey(makeKeywords: string | any[]): string {
        let key = '';
        if (Array.isArray(makeKeywords)) {
            key = makeKeywords.length === 1 ? makeKeywords[0] : undefined;
        } else if (makeKeywords) {
            key = makeKeywords;
        }

        return key;
    }

    getSelectedFacets() {
        this.productsFacade.selectedFacets$.pipe(takeUntil(this.unsubscribe)).subscribe((data) => {
            this.selectedFacets = this.transformValue(data);
            this.cd.markForCheck();
        });
    }

    getIsFetching() {
        this.productsFacade.isFetching$.pipe(takeUntil(this.unsubscribe)).subscribe((data) => {
            this.isFetching = data;
            if (!this.isFetching) {
                this.currentValue = undefined;
                this.currentKey = undefined;
            }
            this.cd.markForCheck();
        });
    }

    setPremiumText(premiumText: Record<string, string> | undefined, filterData?: IFilterParams) {
        const premium = filterData?.['ispremium'];
        this.isPremium = (premium === true || premium === 'true') ?? false;

        if (this.key && premiumText) {
            this.premium = premiumText?.[this.key ?? ''] ?? '';
            this.premiumUrl =
                this.premiumContentSelection?.find((item) => item.make?.toLowerCase().trim() === this.key?.toLowerCase().trim())?.contentLink?.url ??
                '';
        } else if (premiumText) {
            this.premium = premiumText['default'];
            this.premiumUrl = this.premiumContentSelection?.find((item) => item.make === '')?.contentLink?.url || undefined;
        } else {
            this.premium = undefined;
            this.premiumUrl = undefined;
        }

        this.productsFacade.dispatch(
            selectedPremiumText({
                selectedPremiumText: this.premium,
            })
        );
    }

    createSortingList(selectedSort?: IFilterSort) {
        this.sortingsList = [
            {
                title: 'product_list.sorting_new_to_old',
                sort: 'createddate-desc',
                selected: selectedSort === 'createddate-desc',
            },
            {
                title: 'product_list.sorting_price_high_low',
                sort: 'price-desc',
                selected: selectedSort === 'price-desc',
            },
            {
                title: 'product_list.sorting_price_low_high',
                sort: 'price',
                selected: selectedSort === 'price',
            },
        ];

        if (selectedSort) {
            this.modelSorting = this.sortingsList.find((item) => item.selected);
        } else {
            this.modelSorting = this.sortingsList[0];
        }

        this.cd.markForCheck();
    }

    openFilterPanel() {
        this.filterService.openFilterPanel();
    }

    toggleFilterView(view: IFilterView) {
        this.productsFacade.dispatch(layout({ layout: { view } }));
    }

    toggleLeasingView() {
        this.updateQueries({ isleasing: this.isLeasing ? true : undefined });
    }

    togglePremiumView() {
        this.updateQueries({ ispremium: this.isPremium ? true : undefined });
    }

    toggleSortingView() {
        this.updateQueries({ sort: this.modelSorting?.sort });
    }

    updateFilter(key: string, value: string) {
        this.currentKey = key;
        this.currentValue = value;
        this.cd.markForCheck();
        this.updateQueries({ [key]: [value] }, key === 'make.keyword');
    }

    updateQueries(obj: Record<string, any>, clearModels?: boolean) {
        if (clearModels) {
            obj['model.keyword'] = [];
        }

        this.productsFacade.dispatch(
            filterParams({
                filterParams: {
                    ...obj,
                },
            })
        );
    }

    clearAllFilters() {
        const arr = [...new Set(this.selectedFacets.map((item) => item.key))];
        arr.forEach((element) => {
            delete this.filterParams?.[element];
        });
        this.productsFacade.dispatch(filterParams({ filterParams: this.filterParams, resetFilter: true }));
    }

    trackByFacet(index: number, facet: any) {
        return facet?.value || index;
    }

    trackByValue(index: number) {
        return index;
    }

    openSidepanel() {
        if (this.premiumUrl) {
            this.sidePanelService.openContent(this.premiumUrl);
        }
    }

    transformValue(items: ISelectedFacets[]): ISelectedFacets[] {
        return items.map((item) => {
            const facet = { ...item };
            if (facet.key === 'price' || facet.key === 'mileage' || facet.key === 'effect') {
                let firstValue = /[^-]*/i.exec(String(facet?.value))?.[0];
                let secondValue = /-(.*)/i.exec(String(facet?.value))?.[0]?.substring(1);

                if (Number(firstValue)) {
                    firstValue = this.decimalPipe.transform(firstValue, '1.0-0', this.locale) ?? firstValue;
                }

                if (Number(secondValue)) {
                    secondValue = this.decimalPipe.transform(secondValue, '1.0-0', this.locale) ?? secondValue;
                }

                facet.formattedValue = `${firstValue}-${secondValue}`;
            } else {
                facet.formattedValue = facet.value;
            }
            return facet;
        });
    }

    private addStickyObserver(): void {
        if (!(this.stickyTriggerRef && this.featureDetectionService.isBrowser())) {
            return;
        }
        const element: HTMLElement = this.stickyTriggerRef.nativeElement;

        this.stickyButtonsObserver = new IntersectionObserver(
            (entries) => {
                const { isIntersecting } = entries[0];
                this.isButtonsSticky = !isIntersecting;
                this.cd.markForCheck();
            },
            { rootMargin: '0px' }
        );
        this.stickyButtonsObserver.observe(element);
    }

    ngOnDestroy(): void {
        this.unsubscribe.next();
        this.unsubscribe.complete();
        this.stickyButtonsObserver?.disconnect();
    }
}
